import { languages } from '@shared/library/dist/static/language';
import moment from 'moment';
import React from 'react';
import config from '../../../../../../../config';
import './AdministrationPanel.scss';
import {
    create,
    edit,
    get
} from './static/methods';
//#region ICONS
import { ReactComponent as ToolIcon } from "./components/Summary/icons/Tool.svg";
import { ReactComponent as UtentiWebIcon } from "./components/Summary/icons/UtentiWeb.svg";
//#endregion
//#region MODELS
import { GridColDef, GridRenderEditCellParams, GridRowId, GridValidRowModel } from '@mui/x-data-grid';
import { colors, dateColumnSettings, formDataToObject, iconColumnSettings, ISharedConfig, setDialog, setSnackbar } from '@shared/library';
import { IGestDataGridProps } from '@shared/library/dist/components/gest/GestDataGrid/GestDataGridProps';
import { IGestSelectOption, ILabeledID } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';
import { AxiosError } from 'axios';
import {
    IDataGridColumn,
    IGridContent,
    ISummary,
    ISummaryCard,
    ITabTableConfig
} from './models/components/components';
import { Hardware } from './models/entities/Hardware';
import { LangKey } from '../../../../../../../models/ui_data/Lang_model';
import { ILicense } from './models/entities/License';
import { TAllowedCreateEntities } from './models/entities/Shared';
import { IPermission, ISoftwarePermission, IUser } from './models/entities/User';
//#endregion
//#region COMPONENTS
import { CircularProgress } from '@mui/material';
import GestButton from '@shared/library/dist/components/gest/GestInputs/button/GestButton';
import GestSelect from '@shared/library/dist/components/gest/GestInputs/select/GestSelect';
import GestSwitch from '@shared/library/dist/components/gest/GestInputs/switch/GestSwitch';
import DataGridsLayout from './components/DataGridsLayout/DataGridsLayout';
import Summary from './components/Summary/Summary';
import UserCreateForm from './components/UserCreateForm/UserCreateForm';
//#endregion

export type TSection = IUser | ISoftwarePermission | Hardware | ILicense;
export const renderLoading = (loading?: boolean) => loading &&
    <div style={{ display: "flex", justifyContent: "center", alignItems: 'center', width: "100%", height: "100%", position: "absolute", backgroundColor: "white", zIndex: 1 }}>
        <CircularProgress sx={{ color: colors.azure.exaCode }} />
    </div>;

const AdministrationPanel = (props: ISharedConfig) => {
    //#region LOGIC

    //#region STATIC
    const lang = languages(props.settings?.lang);
    document.title = config.app.name;
    //#endregion

    //#region STATE
    const form = React.useRef(null);
    const [clickedArea, setClickedArea] = React.useState<ISummary<TSection>>();
    //USERS
    const [usersTabs, setUsersTabs] = React.useState<IGridContent<IUser>>();
    const [editedField, setEditedField] = React.useState<string>();
    //SOFTWARE PERMISSIONS
    const [permissionsColumns, setPermissionsColumns] = React.useState<GridColDef<ISoftwarePermission>[]>([]);
    //USER CREATE MODAL
    const roles = React.useRef<IGestSelectOption<undefined>[]>([]);
    //LICENSE
    const [licenseTabs, setLicenseTabs] = React.useState<IGridContent<ILicense>>();
    //HARDWARE
    const [hardwareTabs, setHardwareTabs] = React.useState<IGridContent<Hardware>>();
    //FLAGS
    const [loadingUsers, setLoadingUsers] = React.useState(false);
    const [loadingPermissions, setLoadingPermissions] = React.useState(false);
    const [loadingCreate, setLoadingCreate] = React.useState(false);
    const [loadingLicenses, setLoadingLicenses] = React.useState(false);
    const [loadingHardwares, setLoadingHardwares] = React.useState(false);
    //#endregion

    //#region METHODS

    //#region API CALLS
    //#region GET
    const initUsers = () => {
        setLoadingUsers(true);
        return get<IGridContent<IUser>>("Users")
            .then(res => {
                initPermissions(res);

                const userColumns = res.Columns?.filter(c => !c.icon && !(c.name || c.field).toLowerCase().includes("_"));
                const columns = setupColumns(userColumns).map<GridColDef<IUser, string | boolean>>(c => (c.name || c.field).toLowerCase() === "status"
                    ? iconColumnSettings(
                        "Status",
                        params =>
                            <GestSwitch
                                checked={params.value as boolean}
                                onchange={checked => editUserField("User", params.id, params.field, checked)}
                            />,
                        params => `${params.value ? "Disabilita" : "Abilita"} Accesso Web`
                    )
                    : {
                        ...c,
                        editable: true,
                        renderEditCell: c.SelectOptions?.length
                            ? params =>
                                <GestSelect
                                    fullw
                                    value={params.value?.toString()}
                                    options={c.SelectOptions!.map(o => ({
                                        value: (o as ILabeledID).ID,
                                        label: lang[(o as ILabeledID).ID?.toString().toLowerCase() as LangKey]
                                    }))}
                                    onchange={selectedOpts => updateCustomEditing(selectedOpts[0]?.value?.toString(), params)}
                                />
                            : undefined
                        ,
                    }
                );
                const userSection: IGridContent<IUser> = {
                    Tabs: [{ InitRows: res.InitRows! }],
                    Columns: columns,
                    actions: <GestButton label="crea nuovo utente" onclick={openCreateUserForm} />,
                };

                setUsersTabs(userSection);
                setClickedArea({ label: "gestione utenti web", content: userSection } as ISummary<TSection>);
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: "Impossibile recuperare utenti e relativi permessi web", severity: "error" }))
            .finally(() => setLoadingUsers(false));
    };

    const initLicenses = () => {
        setLoadingLicenses(true);
        return get<IGridContent<ILicense>>("Licenses")
            .then(res => {
                const columns = setupColumns(res.Columns).map<GridColDef<ILicense>>(c =>
                    (c.name || c.field).toLowerCase().includes("valid")
                        ? dateColumnSettings(c.name || c.field, lang)
                        : {
                            ...c,
                            valueGetter: (value, row) => {
                                const opt = (c.SelectOptions as ILabeledID[])?.find(o => o.ID === (row as ILabeledID).ID)?.Label;
                                return lang[(opt ?? value) as LangKey] ?? opt ?? value;
                            },
                        }
                );
                setLicenseTabs({ Columns: columns, Tabs: res.Tabs });
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: "Impossibile recuperare licenze", severity: "error" }))
            .finally(() => setLoadingLicenses(false));
    };

    const initHardwares = () => {
        setLoadingHardwares(true);
        return get<IGridContent<Hardware>>("Hardwares")
            .then(res => {
                const columns = setupColumns(res.Columns).map<GridColDef<Hardware>>(c =>
                    (c.name || c.field).toLowerCase().includes("date")
                        ? dateColumnSettings(c.name || c.field, lang)
                        : {
                            ...c,
                            valueGetter: (value, row) => {
                                const opt = (c.SelectOptions as ILabeledID[])?.find(o => o.ID === (row as ILabeledID).ID)?.Label;
                                return lang[(opt ?? value) as LangKey] ?? opt ?? value;
                            },
                        }
                );
                setHardwareTabs({ Columns: columns, Tabs: res.Tabs });
            })
            .catch((err) => setSnackbar(props, { message: "Impossibile recuperare hardwares", severity: "error" }))
            .finally(() => setLoadingHardwares(false));
    };
    //#endregion

    //PATCH
    const editUserField = <T extends IUser | IPermission>(path: string, ID: number | string | bigint, field: string, newValue?: boolean | string | number | bigint) => {
        setLoadingUsers(true);
        return edit<T>(ID, newValue, path, field)
            .then(updatedRow => {
                if (updatedRow) {
                    setSnackbar(props, { message: "Campo modificato con successo", severity: "success" });
                }
                else throw "";
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: "Impossibile modificare questo campo", severity: "error" }))
            .finally(() => setLoadingUsers(false));
    };

    //POST
    const createUser = (evt: React.SubmitEvent<HTMLFormElement>) => {
        evt.preventDefault();

        if (form.current) {
            const newItem = formDataToObject<TAllowedCreateEntities>(form.current);

            if (newItem) {
                setLoadingCreate(true);
                return create<IUser[]>("User", newItem)
                    .then(InitRows => {
                        const userSection: IGridContent<IUser> = {
                            ...usersTabs,
                            Tabs: [{ InitRows }],
                            actions: <GestButton label="crea nuovo utente" onclick={openCreateUserForm} />
                        };
                        setUsersTabs(userSection);
                        setClickedArea({ label: "gestione utenti web", content: userSection, } as ISummary<TSection>);

                        setSnackbar(props, { message: "Creazione utente avvenuta con successo", severity: "success" });
                        setDialog(props);
                    })
                    .catch((err) => setSnackbar(props, { message: "Errore durante la creazione", severity: "error" }))
                    .finally(() => setLoadingCreate(false));
            }
        }
    };
    //#endregion

    const setupColumns = <T extends GridValidRowModel>(columns?: IDataGridColumn<T>[]) => columns?.map<IDataGridColumn<T>>(c => ({
        ...c,
        headerName: (c.name || c.field)?.includes(";")
            ? `${lang[(c.name || c.field).split(";")[0] as LangKey]} (${lang[(c.name || c.field).split(";")[1] as LangKey]})`
            : lang[(c.name || c.field) as LangKey],
        flex: 1,
        maxWidth: 450,
        cellClassName: params => (params.value?.Role || ((c.name || c.field).toLowerCase() === "status" ? params.row.Status : params.value))?.toString().toLowerCase(),
        renderCell: params => lang[(params.value?.Role || params.value) as LangKey] || params.value?.Label,
    })) ?? [];

    const updateCustomEditing = <TRow extends GridValidRowModel, TValue>(value: TValue, params: GridRenderEditCellParams<TRow, TValue>) => {
        const { id, field } = params;

        params.api.setEditCellValue({ id, field, value });
        params.api.stopCellEditMode({ id, field });
    };

    const submitCustomEditing = async <TRow,>(curr: TRow, prev: TRow, params: { rowId: GridRowId; }) => {
        const _ = require('lodash');
        let isUpdated = !_.isEqual(curr, prev);
        let updated = structuredClone(curr);

        if (isUpdated && editedField) {
            let path = "User";
            let field = editedField;
            let newValue: string | number | bigint = (curr as any)[field];
            let ID = params.rowId;

            switch (clickedArea?.label) {
                case "gestione permessi web": {
                    path += `/${params.rowId}/Permission`;
                    field = "Role";

                    const entity = curr as ISoftwarePermission;
                    const value = entity[editedField] as IPermission;
                    newValue = value.Role;
                    ID = value.SoftwareID || permColumns.find(pc => pc.field === editedField)!.ID as GridRowId;

                    updated = { ...curr, [editedField]: value };
                    break;
                }

                case "dettaglio licenze": {
                    path = "License";
                    field = "Terminal";

                    const entity = curr as ILicense;
                    const value = (entity.Terminal as IGestSelectOption<undefined>);
                    newValue = value.value!;

                    updated = { ...curr, [editedField]: { ID: newValue, Label: value.label } };
                    break;
                }

                case undefined: return;
                default: break;
            }

            isUpdated = Boolean(await editUserField(path, ID, field, newValue));
        }

        return isUpdated ? updated : prev;
    };

    const initPermissions = async (res: IGridContent<IUser>) => await new Promise<void>(() => {
        roles.current = res.Columns
            ?.find(c => c.field.toLowerCase() === "role")?.SelectOptions
            ?.map(o => ({
                value: (o as ILabeledID).ID,
                label: lang[(o as ILabeledID).ID?.toString().toLowerCase() as LangKey]
            })) ?? [];

        const permissionColumns = res.Columns?.filter(c => (c.name || c.field).toLowerCase().includes("username") || c.icon || (c.name || c.field).toLowerCase().includes("_")) as GridColDef<ISoftwarePermission>[];
        const softwarePermissionCols = setupColumns(permissionColumns).map<GridColDef<ISoftwarePermission, IPermission | string | number>>(c => ({
            ...c,
            editable: !(c.name || c.field).toLowerCase().includes("username"),
            minWidth: 150,
            renderEditCell: !c.field.toLowerCase().includes("username")
                ? params =>
                    <GestSelect
                        fullw
                        required
                        value={(params.value as IPermission)?.Role}
                        options={roles.current}
                        onchange={selectedOpts => updateCustomEditing({ ...(params.value as IPermission), Role: selectedOpts[0]?.value!.toString() }, params)}
                    />
                : undefined
            ,
        }));

        setPermissionsColumns(softwarePermissionCols);
    });

    //MODALS
    const openCreateUserForm = () => setDialog(props, {
        label: "crea nuovo utente",
        content:
            <form ref={form} onSubmit={createUser}>
                <UserCreateForm roles={roles.current} />
                <GestButton fullw submit label="crea" h={40} loading={loadingCreate} />
            </form>
    });
    //#endregion

    //STATE CHANGE
    React.useEffect(() => {
        initUsers();
        initLicenses();
        initHardwares();
    }, []);

    //#region RENDER
    const usrColumns = React.useMemo(() => lang && setupColumns(usersTabs?.Columns).map(c => (c.name || c.field).toLowerCase() === "status"
        ? iconColumnSettings<IUser, boolean>(
            "Status",
            params =>
                <GestSwitch
                    checked={params.value}
                    onchange={checked => editUserField("User", params.id, params.field, checked)}
                />,
            params => `${params.value ? "Disabilita" : "Abilita"} Accesso Web`
        )
        : c
    ), [lang]);

    const permColumns = React.useMemo(() => lang ? setupColumns(permissionsColumns) : [], [lang]);

    const licenseColumns = React.useMemo(() => lang ? setupColumns(licenseTabs?.Columns) : [], [lang]);

    const hardwareColumns = React.useMemo(() => lang ? setupColumns(hardwareTabs?.Columns) : [], [lang]);

    const cardsConfig = React.useMemo<ISummaryCard[]>(() => [
        {
            title: "utenti web",
            actions: <GestButton
                label="gestisci"
                color='#FFD685'
                active={clickedArea?.label === "gestione utenti web"}
                onclick={() => setClickedArea({
                    label: "gestione utenti web",
                    content: {
                        ...usersTabs,
                        Columns: usrColumns as IDataGridColumn<TSection>[],
                        actions: <GestButton label="crea nuovo utente" onclick={openCreateUserForm} />
                    },
                })}
            />,
            sections: [
                {
                    stats: {
                        title: "attivi/disponibili",
                        icon: <UtentiWebIcon />,
                        value: usersTabs?.Tabs?.[0].InitRows.filter(u => u.Status).length ?? 0,
                        max: usersTabs?.Tabs?.[0].InitRows.length ?? 0,
                    },
                },

            ],
        },
        {
            title: "permessi web",
            actions: <GestButton
                label="gestisci"
                color='#FFB780'
                active={clickedArea?.label === "gestione permessi web"}
                onclick={() => setClickedArea({
                    label: "gestione permessi web",
                    content: {
                        Columns: permColumns as IDataGridColumn<TSection>[],
                        Tabs: usersTabs?.Tabs?.map<ITabTableConfig<ISoftwarePermission>>(t => ({ ...t, InitRows: t.InitRows.filter(u => u.Permissions).map(u => ({ ...u.Permissions!, id: u.ID, Username: u.Username } as ISoftwarePermission)) }))
                    },
                })}
            />,
            sections: [{
                stats: {
                    title: "assegnati/disponibili",
                    icon: <ToolIcon />,
                    max: permColumns.length * (usersTabs?.Tabs?.[0].InitRows.length ?? 0),
                    value: usersTabs?.Tabs?.[0]?.InitRows
                        .filter(u => u.Permissions)
                        .flatMap(u => Object.keys(u.Permissions!).filter(p => (u.Permissions as any)[p] !== "disabled").length)
                        .reduce((prev, curr) => prev += curr, 0) ?? 0
                },
            }],
        },
        {
            title: "licenze attive/disponibili",
            actions: <GestButton
                label="dettaglio"
                color='#00A0DF'
                active={clickedArea?.label === "dettaglio licenze"}
                onclick={() => setClickedArea({
                    label: "dettaglio licenze",
                    content: { ...licenseTabs, Columns: licenseColumns as IDataGridColumn<TSection>[] }
                })}
            />,
            sections: licenseTabs?.Tabs?.map(t => ({
                stats: {
                    title: t.TabName ?? "licenza",
                    max: t.InitRows.length,
                    value: t.InitRows.filter(l => !moment(new Date()).isAfter(l.ValidTo)).length,
                    icon: <img src={require(`./components/Summary/icons/${t.TabName?.toLowerCase()}.svg`)} alt={`icon-${t.TabName}`} />,
                }
            })) ?? [],
        },
        {
            title: "hardwares attivi/disponibili",
            actions: <GestButton
                label="dettaglio"
                color='#00A66D'
                active={clickedArea?.label === "dettaglio hardware"}
                onclick={() => setClickedArea({
                    label: "dettaglio hardware",
                    content: { ...hardwareTabs, Columns: hardwareColumns as IDataGridColumn<TSection>[] }
                })}
            />,
            sections: hardwareTabs?.Tabs?.map(t => ({
                stats: {
                    title: t.TabName ?? "hardware",
                    max: t.InitRows.length,
                    value: t.InitRows.filter(h => h.Status !== "replaced" && h.Status !== "returned").length,
                    icon: <img src={require(`./components/Summary/icons/${t.TabName?.toLowerCase()}.svg`)} alt={`icon-${t.TabName}`} />,
                }
            })) ?? [],
        },
    ], [usersTabs,
        usrColumns,
        permColumns,
        licenseTabs,
        licenseColumns,
        hardwareTabs,
        hardwareColumns,
    ]);
    //#endregion    
    //#endregion

    return props.session?.userData?.role !== "admin" ? <h2>Accesso negato a questa area</h2> : (
        <div id="AdministrationPanel">
            <Summary {...{
                cardsConfig,
                currSection: clickedArea,
                setCurrSection: setClickedArea,
            }} />

            {renderLoading(
                (clickedArea?.label === "dettaglio hardware" && loadingHardwares) ||
                (clickedArea?.label === "dettaglio licenze" && loadingLicenses) ||
                loadingUsers
            )}

            <DataGridsLayout
                area={clickedArea}
                gridProps={{
                    columns: [],
                    onCellEditStart: params => setEditedField(params.field),
                    processRowUpdate: submitCustomEditing,
                    getRowClassName: params => clickedArea?.label === "dettaglio licenze" && moment(new Date()).isAfter((params.row as ILicense).ValidTo)
                        ? "draftRemove"
                        : ""
                } as IGestDataGridProps<TSection>}
            />
        </div>
    );
};
export default React.memo(AdministrationPanel);